// src/utils/reports.js
import * as _ from 'lodash';
import { AutoScale, ReportTypesId, ScaleTypesId, PowerScale } from 'utils/enums/report';
import IntervalCatalog from 'utils/enums/interval';
import { formatSI } from 'utils/systemDiagram';
import { ScaleValues, ScaleSymbol } from 'utils/enums/unitMeasurement';
import { getMonthNameByNumber } from 'utils/date';
import messages from '../containers/Charting/messages';
import { getValueIfExists } from './propertyValidation';

const twelveHoursLimit = 12;
const minutesLimit = 60;
const defaultYTitle = 'yAxisTitle';

export const IntervalCurrentMonth = 'current_month';

const chartByDaySettings = {
  offsetLabels: 20,
  xAxisTitleMarginTop: 10,
  xAxisLabelRotate: 45,
};

/**
 * Report Types catalog
 */
export const reportTypes = [
  {
    id: ReportTypesId.Facility,
    name: 'Energy Overview',
  },
  {
    id: ReportTypesId.Energy,
    name: 'Energy Production',
  },
  {
    id: ReportTypesId.ESS,
    name: 'ESS State',
  },
  {
    id: ReportTypesId.VehicleAndFleet,
    name: 'Vehicle and Fleet Loads',
  },
  {
    id: ReportTypesId.Monthly,
    name: 'Monthly Report',
  },
];

/**
 * getReportTypesSelectOptions
 * Transform Report Types catalog into dropdown structure, and validate if resetFilters
 * has a "true" value to delete Monthly Report the last row. The validation apply for only display
 * the monthly reports on site/reports and no in report generals, this because affect performance on
 * the endpoints load many data and the monthly report is focus on one site.
 * @param {boolean} resetFilters receives resetFilter variable
 * @return {Array} Report Types dropdown options
 */
export const getReportTypesSelectOptions = () => {
  const reportTypesClone = _.clone(reportTypes);
  return reportTypesClone.map((reportType) => ({
    value: reportType.id,
    text: reportType.name,
  }));
};

/**
 * getXTitle
 * Transform xAxisTitle depending interval and return text from messages
 * @param {func} formatMessage receives formatMessage function to format messages
 * @param {string} interval receives interval from search data
 * @returns string
 */
export const getXTitle = (formatMessage, interval) => {
  switch (interval) {
    case IntervalCatalog.day:
      return formatMessage(messages.xAxisTitleHours);
    case IntervalCatalog.month:
    case IntervalCurrentMonth:
      return formatMessage(messages.xAxisTitleDays);
    default:
      return '';
  }
};

/**
 * @method getYTitle
 * Transform yAxisTitle depending interval validating if have scale "Auto":
 * - If have maxValue more than 100000 set to variable the value "GWh"
 * - If have maxValue more than 1000 set to variable the value "MWh"
 * - If have maxValue less than 1000 set to variable the value "kWh"
 * else have another scale return value from ScaleTypesId position
 * @param {func} formatMessage receives formatMessage function to format messages
 * @param {string} scale receives scale from search data
 * @param {number} maxValue receives max value from data
 * @param {string} yAxisTitle receives y axis title
 * @returns string
 */
export const getYTitle = (formatMessage, scale, maxValue = 0, yAxisTitle = defaultYTitle) => {
  const typeScale = yAxisTitle === defaultYTitle ? AutoScale : PowerScale;
  let scaleTitle = typeScale.kw;
  if (scale === ScaleTypesId.Auto) {
    if (maxValue >= ScaleValues.Giga) {
      scaleTitle = typeScale.gw;
    } else if (maxValue >= ScaleValues.Mega) {
      scaleTitle = typeScale.mw;
    }
  } else {
    scaleTitle = ScaleTypesId[`${scale}`];
  }

  return `${formatMessage(messages[`${yAxisTitle}`])} (${scaleTitle})`;
};

/**
 * getTimePeriodTitle
 * Transform timePeriod depending interval and return timePeriod with format
 * @param {object} params receives search params
 * @param {string} params.timePeriod receives time period param (YYYY-MM-DD or YYYY-MM)
 * @param {object} params.interval receives interval type param ({ value: string, text: string })
 * @param {object} params.timePeriodSelected receives selected time period object { value: string, text: string }
 * @returns string

/**
 * getReportSitesSelectOptions
 * Transform Report Sites catalog into dropdown structure
 * @param {Array} sites receives dropdown options
 * @return {Array} return sites options
 */
export const getReportSitesSelectOptions = (sites) =>
  sites.map((site) => ({
    value: site.siteId,
    text: site.name,
  }));

/**
 * getReportIntervalSelectOptions
 * Transform Report Interval catalog into dropdown structure
 * MODIFIED: Return only "Current Month" and "Date Range"
 * @return {Array} return interval options
 */
export const getReportIntervalSelectOptions = () => [
  {
    text: 'Last 30 Days',
    value: IntervalCurrentMonth,
  },
  {
    text: `Custom`,
    value: IntervalCatalog.custom,
  },
];

/**
 * getReportScaleSelectOptions
 * Transform Report Scale catalog into dropdown structure
 * @return {Array} return scale options
 */
export const getReportScaleSelectOptions = () =>
  Object.keys(ScaleTypesId).map((val) => ({
    text: ScaleTypesId[`${val}`],
    value: val,
  }));

export const getTimePeriodTitle = (params) => {
  const { interval = {}, timePeriodSelected = {} } = params;
  const defaultDate = new Date();

  switch (interval.value) {
    case IntervalCurrentMonth:
      // For current month, display Month-Year
      return `${getMonthNameByNumber(
        defaultDate.getUTCMonth() + 1,
        true,
      )}-${defaultDate.getUTCFullYear()}`;
    case IntervalCatalog.day:
      return getValueIfExists(() => timePeriodSelected.text, '');
    default:
      return '';
  }
};

/**
 * getMaxValueFromChartLevels
 * Get max value from charts with multi levels, we need to search in all nested nodes to find max value
 * @param {object} chartData chart to get max value
 * @returns {number} a number with max value found
 */
export const getMaxValueFromChartLevels = (chartData) => {
  if (getValueIfExists(() => chartData.length, 0) > 0) {
    const nodeMaxValue = _.maxBy(
      _.flatMap(chartData, ({ values }) => _.flatten(values)),
      (item) => item.value,
    );
    return getValueIfExists(() => +nodeMaxValue.value, 0);
  }
  return 0;
};

/**
 * @method generateCompaniesOptions
 * Format companies options dropdown array with option properties value and text,
 * from companies sites array
 * @param {array} companies receives companies sites array data
 * @returns {array}
 */
export const generateCompaniesOptions = (companies) =>
  companies.map((company, index) => ({
    value: index,
    text: company.companyName,
  }));

/**
 * @method generateSitesOptions
 * Format sites options dropdown array with option properties value and text
 * @param {array} sites receives companies sites array data
 * @returns {array}
 */
export const generateSitesOptions = (sites) =>
  sites.map((site) => ({
    value: site.siteId,
    text: `${site.name} - ${site.locationName}`,
  }));

/**
 * @method getChartSettingsByPeriod
 * Gets chart UI Settings
 * @param {numeric} period indicates the ID for IntervalCatalog
 * @param {boolean} isGrouped
 * @returns {array}
 */
export const getChartSettingsByPeriod = (period, isGrouped) => {
  let chartSettings;

  switch (period) {
    case IntervalCatalog.day:
      chartSettings = chartByDaySettings;

      if (isGrouped) {
        chartSettings = { ...chartByDaySettings, barWidth: 6 };
      }
      break;
    default:
      chartSettings = {};
      break;
  }

  return chartSettings;
};

/**
 * @method scaledFormatYAxis
 * Handle format to display depending the value dividing when is GW, MW, KW or W the values,
 * but if the interval have value or isn't GW, MW, KW or W  should display normal value
 * @param {number} intervalValue receives interval value
 * @param {number} maxValue receives maxValue from data
 * @param {number} value receives Y value
 * @returns {number}
 */
export const scaledFormatYAxis = (intervalValue, maxValue, value, roundDecimals) => {
  if (maxValue > ScaleValues.Giga && !intervalValue) {
    return formatSI(value, ScaleSymbol.Giga, roundDecimals, ScaleValues.Kilo, false);
  }
  if (maxValue > ScaleValues.Mega && !intervalValue) {
    return formatSI(value, ScaleSymbol.Mega, roundDecimals, ScaleValues.Kilo, false);
  }
  return formatSI(value, ScaleSymbol.Kilo, roundDecimals, ScaleValues.Kilo, false);
};

/**
 * getSafeHeight
 * @param {number} valueToSubtract - the value that will be taken away from the chartHeight
 * @param {number} chartHeight - the current height of the chart
 * @returns number (not allowed to be negative)
 */
export const getSafeHeight = (valueToSubtract, chartHeight) =>
  chartHeight && valueToSubtract ? Math.max(0, chartHeight - valueToSubtract) : 0;

/**
 * @method generateTimeTwelveHours
 * Generate an array for 12 hours select options
 * @returns {array}
 */
export const generateTimeTwelveHours = () => {
  const hours = [];
  for (let hoursCount = 1; hoursCount <= twelveHoursLimit; hoursCount += 1) {
    hours.push({
      value: hoursCount,
      text: hoursCount,
    });
  }
  return hours;
};

/**
 * @method generateTimeMinutes
 * Generate an array for 60 minutes select options
 * @returns {array}
 */
export const generateTimeMinutes = () => {
  const minutes = [];
  for (let minutesCount = 0; minutesCount < minutesLimit; minutesCount += 1) {
    const values = minutesCount < 10 ? `0${minutesCount}` : minutesCount;
    minutes.push({
      value: values,
      text: values,
    });
  }
  return minutes;
};

export const TimeDivisionOptions = [
  {
    value: 'AM',
    text: 'AM',
  },
  {
    value: 'PM',
    text: 'PM',
  },
];
